import { h } from '@omni-torn/ui';
import { KEY_LEVEL_NAMES, OBSERVATIONS_KEPT_TERMS, sectionState, WEB_SECTIONS } from '@omni-torn/shared';
import { extension } from '../bridge';
import { navigateTo } from '../nav';
import { handOverKey, loadMe, markTrackingSeen, recheckKey, server, session, signOut, upgradeKey } from '../session';
import { viewer } from '../shell';
import { box, facts, keyMakeLink, lockIcon, tct } from '../ui';
import { sharingBox } from './faction';
import { forgetTermsShown } from '../terms-gate';
import { keysBox, termsFullTable, termsTable } from './keys';
import { lendBox } from './lend';
import { spyShareSwitch } from './spies';
import { reportsBox } from './tickets';
import { savePrefs } from './shared';
import { snapshotNow } from '../sync';
import { accountTabs, renderAccountData } from './account-data';

const LEVEL_SOURCE: Record<string, string> = { 'built-in': 'built in', 'player-grant': 'given to you by the owner', 'faction-grant': 'given to your faction', subscription: 'given to you by the owner', none: '' };

/** A button that asks "Sure?" on the first click and acts on the second. */
function twoStep(label: string, sure: string, act: () => Promise<void>): HTMLElement {
  const b = h('button', { type: 'button', class: 'btn-danger' }, label);
  let armed = false;
  b.addEventListener('click', async () => {
    if (!armed) {
      armed = true;
      b.textContent = sure;
      setTimeout(() => {
        armed = false;
        b.textContent = label;
      }, 5000);
      return;
    }
    b.disabled = true;
    await act().catch(() => {});
    b.disabled = false;
  });
  return b;
}

export async function renderAccount(view: HTMLElement, path = '/account'): Promise<void> {
  // Fresh: the extension may have changed your choices since this page loaded.
  await loadMe().catch(() => null);
  // Account → Data (S40): backup, restore, delete by range.
  if (/^\/account\/data\/?$/.test(path)) return renderAccountData(view);
  const s = session.get()!;
  const me = session.me();
  const v = viewer();
  const key = s.access;

  // ---- key
  const keyInput = h('input', { type: 'password', autocomplete: 'off', maxlength: '16', placeholder: 'New key (16 characters)', 'aria-label': 'New API key' }) as HTMLInputElement;
  const keyStatus = h('p', { class: 'form-status', role: 'status' });
  const keyForm = h('form', { class: 'key-row' }, keyInput, h('button', { type: 'submit', class: 'btn-primary' }, 'Agree and use this key'));
  keyForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    keyStatus.className = 'form-status';
    keyStatus.textContent = 'Checking with Torn…';
    try {
      const a = await upgradeKey(keyInput.value);
      keyInput.value = '';
      await loadMe();
      keyStatus.className = 'form-status ok';
      keyStatus.textContent = `Now using a ${a.custom ? 'custom' : KEY_LEVEL_NAMES[a.level]} key.`;
      navigateTo('/account');
    } catch (err) {
      keyStatus.className = 'form-status bad';
      keyStatus.textContent = (err as Error).message;
    }
  });
  const recheck = h('button', { type: 'button', class: 'btn-small' }, 'Check my key again');
  recheck.addEventListener('click', async () => {
    recheck.disabled = true;
    try {
      await recheckKey();
      navigateTo('/account');
    } catch (err) {
      keyStatus.className = 'form-status bad';
      keyStatus.textContent = (err as Error).message;
      recheck.disabled = false;
    }
  });
  const opens = h(
    'ul',
    { class: 'opens' },
    ...WEB_SECTIONS.filter((x) => !x.always && x.access !== 'owner').map((sec) => {
      const st = sectionState(sec, v);
      return h('li', { class: st.open ? 'ok' : 'no' }, st.open ? h('span', { class: 't-green' }, '✓ ') : lockIcon(), ` ${sec.name}`, st.open ? null : h('span', { class: 'muted small' }, ` — ${st.text}`));
    }),
  );

  // ---- history
  const hist = me?.prefs.history;
  const histStatus = h('span', { class: 'save-status', role: 'status' });
  const histToggle = h('input', { type: 'checkbox', checked: hist !== false }) as HTMLInputElement;
  histToggle.addEventListener('change', async () => {
    histStatus.textContent = 'Saving…';
    try {
      await savePrefs({ history: histToggle.checked });
      if (histToggle.checked && !(me?.tracking.tracked && me.tracking.status === 'ok')) await snapshotNow().catch(() => false);
      await loadMe();
      histStatus.className = 'save-status ok';
      histStatus.textContent = histToggle.checked ? 'On — collecting from now on.' : 'Off — what was kept is deleted.';
    } catch (err) {
      histStatus.className = 'save-status bad';
      histStatus.textContent = (err as Error).message;
    }
  });

  // ---- background tracking
  const t = me?.tracking;
  // No switch of its own any more (owner, 2026-11-02: "One button should cover both"): a key given (Your key → Agree and
  // use my key) serves your account and your faction; "Remove my key everywhere" or "Delete my data" stops both. Players
  // who switched tracking off before stay off until they give a key again.
  const levelName = (n: number | null) => (n ? ['', 'Public Only', 'Minimal Access', 'Limited Access', 'Full Access'][n] ?? '' : '');
  const trackLines = !t?.enabled
    ? [h('p', { class: 'muted' }, 'Background tracking is off on this server.')]
    : [
        h(
          'p',
          { class: 'muted' },
          'Omni-Torn keeps the key you gave on its server — encrypted — and keeps your history, fights and log up to date even when this site and the extension are closed, and shares with your faction. Only you see your own data. It’s part of giving your key (one agreement for your account and your faction); to stop it, remove your key everywhere (below) or delete your data — the server deletes the key at once.',
        ),
        me?.prefs.track === false
          ? h('p', { 'data-tracking': 'off' }, 'Off — the server has no key of yours. Give your key again (Your key → “Agree and use my key”) to start it.')
          : t.tracked && t.status === 'ok'
            ? h('p', { 'data-tracking': 'on' }, h('span', { class: 't-green' }, '✓ '), `Tracking with your ${levelName(t.accessLevel)} key`, t.lastOkAt ? ` · last update ${tct(t.lastOkAt)} TCT` : ' · first update within a minute', '.', t.attacks ? ` ${t.attacks} fights kept.` : '', t.logs ? ` ${t.logs} log entries kept.` : '')
            : t.status === 'paused'
              ? h('p', { class: 'notice' }, `Paused: ${t.problem ?? ''}`)
              : t.status === 'bad'
                ? h('p', { class: 'notice bad' }, `${t.problem ?? 'The kept key stopped working.'} `, h('a', { href: '/account#key', 'data-link': true }, 'Use another key'))
                : h('p', { class: 'notice' }, 'Not tracking yet — the server needs your key once. ', h('button', { type: 'button', class: 'btn-small', onclick: async () => (markTrackingSeen(), (await handOverKey(true)) ? (await loadMe(), navigateTo('/account#tracking')) : null) }, 'Start tracking')),
      ];

  // ---- extension
  const extBox = h('div', {}, h('p', { class: 'muted' }, 'Looking for the extension…'));
  void extension(true).then((ext) => {
    extBox.replaceChildren(
      !ext
        ? h('p', {}, 'Not in this browser. With it, Torn’s own pages show all of this too, and it keeps your history and faction share up to date in the background.')
        : !ext.signedIn
          ? h('p', {}, `Omni-Torn ${ext.version} is installed but not signed in (its options page).`)
          : ext.playerId === s.playerId
            ? h('p', {}, h('span', { class: 't-green' }, '✓ '), `Omni-Torn ${ext.version} is signed in as you. It follows the history and sharing choices above, and keeps them going while your browser is open.`)
            : h('p', { class: 'notice' }, `The extension is signed in as someone else (${ext.name ?? ext.playerId}).`),
    );
  });

  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Account & sync'), accountTabs('account')),
    box('You', facts([
      ['Player', `${s.name} [${s.playerId}]`],
      ['Faction', me?.factionId ? `#${me.factionId}` : 'None'],
      ['Omni-Torn level', h('span', {}, h('span', { class: `level-badge level-${me?.level.level ?? 'basic'}` }, me?.level.level ?? 'basic'), me?.level.source && LEVEL_SOURCE[me.level.source] ? h('span', { class: 'muted' }, ` — ${LEVEL_SOURCE[me.level.source]}${me.level.until ? `, until ${tct(me.level.until)}` : ''}`) : null)],
      ['Signed in until', `${tct(s.until)} TCT (renews by itself)`],
    ])),
    h(
      'section',
      { class: 'panel', id: 'key' },
      h('header', { class: 'panel-head' }, h('h2', {}, 'Your key')),
      h(
        'div',
        { class: 'panel-body' },
        facts([
          ['Kept', `${s.mode === 'extension' ? 'in the Omni-Torn extension (this site never sees it)' : session.remembered() ? 'in this browser, encrypted (remembered on this device)' : 'in this browser tab, encrypted (until you close it)'}${me?.tracking.tracked ? ' — and on the Omni-Torn server, encrypted, for background tracking' : ''}`],
          ['Access', key ? (key.custom ? `Custom (${(key.selections.user ?? []).length} user selections)` : key.type) : 'unknown'],
        ]),
        h('h3', {}, 'What it opens'),
        opens,
        key && key.level < 3 && !key.custom ? h('p', {}, 'Recommended: ', keyMakeLink(3), ', then paste it below. (Full adds only your activity log, the Logs section.)') : key && key.level === 3 && !key.custom ? h('p', { class: 'muted' }, 'A Limited key is what Omni-Torn recommends. Full adds only your activity log (Logs): ', keyMakeLink(4), '.') : null,
        h('h3', {}, s.mode === 'extension' ? 'Use a key on this site instead' : 'Use a different key'),
        // Torn's terms: the table wherever a key is given.
        h('div', { class: 'terms' }, h('h4', {}, 'What Omni-Torn does with your key (Torn’s API terms)'), termsTable(), h('details', { class: 'terms-every' }, h('summary', {}, 'Show every detail'), termsFullTable())),
        keyForm,
        keyStatus,
        h('p', {}, recheck, h('span', { class: 'muted small' }, ' (after changing the key’s access on Torn)')),
      ),
    ),
    h('section', { class: 'panel', id: 'tracking' }, h('header', { class: 'panel-head' }, h('h2', {}, 'Background tracking')), h('div', { class: 'panel-body' }, ...trackLines)),
    keysBox(),
    lendBox(),
    h(
      'section',
      { class: 'panel', id: 'history' },
      h('header', { class: 'panel-head' }, h('h2', {}, 'History')),
      h(
        'div',
        { class: 'panel-body' },
        h('label', { class: 'switch' }, histToggle, h('strong', {}, ' Keep a history of my stats'), histStatus),
        h('p', { class: 'muted' }, 'About once an hour (in the background with tracking on, else while this site or the extension is open): personal stats, level, and — with a Limited key or better — battle stats, networth and money. Hourly for a week, then one a day — from terms 19 kept until you delete it (before 19: up to 400 days). Only you can see it. Switching it off deletes it.'),
        me?.history.count ? h('p', {}, `${me.history.count} snapshots kept, since ${tct(me.history.first ?? Date.now())} TCT.`) : null,
      ),
    ),
    sharingBox(),
    h('section', { class: 'panel', id: 'spies' }, h('header', { class: 'panel-head' }, h('h2', {}, 'Your spies')), h('div', { class: 'panel-body' }, spyShareSwitch())),
    reportsBox(),
    box('The Omni-Torn extension', extBox),
    box('Your data', [
      // From terms 12 (OBSERVATIONS_KEPT_TERMS) what they saw of other players stays, without them (apps/api/src/delete-data.ts).
      h(
        'p',
        {},
        (me?.prefs.terms ?? 0) >= OBSERVATIONS_KEPT_TERMS
          ? 'Delete your data on Omni-Torn: your key everywhere (background tracking, “Your faction”, lending), your history, your own fights list and log, your settings and lists here, what you share with your faction (your entry, pass rates, calls, your loadout), what you sent to the player database about yourself, your bug reports and suggestions, and the Discord bot’s link of your Discord to your Torn account. What you saw of other players stays without your name — sightings, flights, faction moves, loadouts, spies kept for your faction or shared with every Omni-Torn user; your fights stay in the player database as Torn showed them (both sides named), for Omni-Torn’s estimates (other players see only estimates) — and so do your faction’s records that name you (whichever member’s key read them) and others’ sightings of you. Your fair-fight readings stay in the anonymous pool without your id. Tracking and history stay off afterwards until you switch them back on. The same button is in the extension (options → Account), in Torn PDA (Torn’s Settings → API Keys) and in Discord (/account delete).'
          : 'Delete everything Omni-Torn keeps about you: your key everywhere (background tracking, “Your faction”, lending), your history, kept fights and log, your settings and lists here, what you share with your faction (your entry, pass rates, calls, loadouts), your bug reports and suggestions, and the Discord bot’s link of your Discord to your Torn account. Your fair-fight readings stay in the anonymous pool without your id. Tracking and history stay off afterwards until you switch them back on. The same button is in the extension (options → Account), in Torn PDA (Torn’s Settings → API Keys) and in Discord (/account delete).',
      ),
      h('p', {}, 'To keep a copy first, put one back, or delete only part of your history: ', h('a', { href: '/account/data', 'data-link': true, 'data-link-to': 'data' }, 'Account → Data'), '.'),
      twoStep('Delete my data', 'Click again to delete it all', async () => {
        const r = await server<{ tickets?: number; discord?: string }>('DELETE', '/api/me');
        // Nothing agreed is left: the banner's "seen" in this browser counts for nothing now (app/terms-gate.ts).
        forgetTermsShown();
        if (r?.discord === 'unreachable') alert('Deleted. The Discord bot didn’t answer, so your Discord link is still there — use /unlink in Discord to drop it.');
        await loadMe();
        navigateTo('/account');
      }),
    ]),
    box('Sign out', [
      h('p', {}, h('button', { type: 'button', class: 'btn-small', onclick: () => signOut() }, 'Sign out of this browser'), h('span', { class: 'muted small' }, ' Forgets your key and sign-in here. Nothing you’ve kept is deleted.')),
      h('p', {}, twoStep('Sign out everywhere', 'Click again to end every sign-in', async () => {
        await server('POST', '/api/session/signout').catch(() => null);
        signOut('Signed out everywhere.');
      }), h('span', { class: 'muted small' }, ' Ends every Omni-Torn sign-in, on all your browsers. Nothing is deleted: the key kept for background tracking stays, unused until you sign in again (to delete it: “Remove my key everywhere”, or “Delete my data”). The extension keeps its own key and signs back in by itself — to stop that, sign out on its options page, or switch tracking off above.')),
    ]),
  );
  if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
}
